import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, imgUrl, rupee } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Heart, Star, Truck, RefreshCw, ShieldCheck, Minus, Plus, BadgeCheck } from "lucide-react";
import { waMessage } from "@/components/WhatsAppButton";
import { toast } from "sonner";

const ReviewForm = ({ productId, onAdded }) => {
  const { user } = useStore();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!rating) { toast.error("Please select a star rating"); return; }
    setBusy(true);
    try { const r = await api.post("/reviews", { product_id: productId, rating, comment }); onAdded(r.data); setRating(0); setComment(""); toast.success("Thank you for your review!"); }
    catch { toast.error("Could not submit review"); } finally { setBusy(false); }
  };
  if (!user) return <p className="mt-3 text-sm text-muted-text" data-testid="review-login-prompt"><Link to="/login" className="text-burgundy underline">Log in</Link> to rate this product.</p>;
  return (
    <div className="mt-4 border border-border bg-cream/60 p-4" data-testid="review-form">
      <p className="text-sm font-medium">Rate this product</p>
      <div className="mt-2 flex gap-1" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} data-testid={`review-star-${n}`}><Star className={`h-6 w-6 ${n <= (hover || rating) ? "fill-amber-400 text-amber-400" : "text-border"}`} /></button>)}
      </div>
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Share what you loved (optional)" className="mt-3 w-full border border-border bg-white p-2 text-sm outline-none focus:border-burgundy" data-testid="review-comment" />
      <Button onClick={submit} disabled={busy} className="mt-2 rounded-none bg-burgundy" data-testid="review-submit">{busy ? "Submitting..." : "Submit Review"}</Button>
    </div>
  );
};

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, inWishlist, settings } = useStore();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then((r) => {
      setData(r.data); setActive(0); setQty(1);
      setSize(""); setColor(r.data.product.colors?.[0]?.name || "");
    }).catch(() => setData("error"));
  }, [slug]);

  if (data === "error") return <div className="py-24 text-center font-serif text-2xl">Product not found</div>;
  if (!data) return <div className="mx-auto max-w-7xl px-6 py-16"><div className="grid gap-10 md:grid-cols-2"><div className="aspect-[3/4] animate-pulse bg-muted" /><div className="space-y-4"><div className="h-8 w-2/3 animate-pulse bg-muted" /><div className="h-6 w-1/3 animate-pulse bg-muted" /></div></div></div>;

  const p = data.product;
  const discount = p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;
  const wished = inWishlist(p.id);
  const gallery = p.images?.length ? p.images : [p.primary_image];

  const handleAdd = () => {
    if (p.sizes?.length && !size) { toast.error("Please select a size"); return; }
    addToCart(p, qty, size, color);
  };
  const handleBuy = () => { handleAdd(); navigate("/checkout"); };
  const waText = `Hello ANUSWA, I am interested in ${p.name}. Please share more details about availability, size and price.`;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <nav className="mb-6 text-xs text-muted-text">
        <Link to="/" className="hover:text-burgundy">Home</Link> / <Link to={`/collections/${p.category.toLowerCase().replace(/ /g, "-")}`} className="hover:text-burgundy">{p.category}</Link> / <span className="text-charcoal">{p.name}</span>
      </nav>

      <div className="grid gap-10 lg:grid-cols-2">
        {/* Gallery */}
        <div className="flex gap-4">
          <div className="flex flex-col gap-3">
            {gallery.map((g, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`} className={`h-20 w-16 overflow-hidden border ${active === i ? "border-burgundy" : "border-border"}`}>
                <img src={imgUrl(g)} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
          <div className="group relative flex-1 overflow-hidden bg-muted">
            <img src={imgUrl(gallery[active])} alt={p.name} className="h-full max-h-[600px] w-full object-cover transition-transform duration-500 group-hover:scale-125" data-testid="product-main-image" />
            {discount > 0 && <span className="absolute left-0 top-4 bg-burgundy px-3 py-1 text-sm text-white">{discount}% OFF</span>}
          </div>
        </div>

        {/* Info */}
        <div>
          <h1 className="font-serif text-3xl text-charcoal">{p.name}</h1>
          <p className="mt-1 text-xs uppercase tracking-widest text-muted-text">SKU: {p.sku}</p>
          <div className="mt-3 flex items-center gap-2">
            <div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className={`h-4 w-4 ${i < Math.round(p.rating) ? "fill-amber-400 text-amber-400" : "text-border"}`} />)}</div>
            <span className="text-sm text-muted-text">({p.rating}) {p.reviews_count} Reviews</span>
          </div>
          <div className="mt-4 flex items-baseline gap-3">
            <span className="font-serif text-3xl text-burgundy">{rupee(p.price)}</span>
            {p.mrp > p.price && <><span className="text-lg text-muted-text line-through">{rupee(p.mrp)}</span><span className="text-sm font-medium text-green-600">{discount}% OFF</span></>}
          </div>

          {p.sizes?.length > 0 && (
            <div className="mt-6">
              <p className="mb-2 text-sm font-medium">Size</p>
              <div className="flex flex-wrap gap-2">
                {p.sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`} className={`min-w-[44px] border px-3 py-2 text-sm ${size === s ? "border-burgundy bg-burgundy text-white" : "border-border hover:border-burgundy"}`}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {p.colors?.length > 0 && (
            <div className="mt-5">
              <p className="mb-2 text-sm font-medium">Color: <span className="text-muted-text">{color}</span></p>
              <div className="flex gap-2">
                {p.colors.map((c) => (
                  <button key={c.name} onClick={() => setColor(c.name)} data-testid={`color-${c.name}`} aria-label={c.name} className={`h-8 w-8 rounded-full border-2 ${color === c.name ? "border-burgundy" : "border-border"}`} style={{ backgroundColor: c.hex }} />
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border border-border">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="grid h-11 w-11 place-items-center" data-testid="qty-dec"><Minus className="h-4 w-4" /></button>
              <span className="w-10 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="grid h-11 w-11 place-items-center" data-testid="qty-inc"><Plus className="h-4 w-4" /></button>
            </div>
            <Button onClick={handleAdd} data-testid="add-to-cart-btn" className="h-11 flex-1 rounded-none bg-burgundy uppercase tracking-widest hover:bg-burgundy/90">Add to Cart</Button>
          </div>
          <div className="mt-3 flex gap-3">
            <Button onClick={handleBuy} data-testid="buy-now-btn" className="h-11 flex-1 rounded-none bg-charcoal uppercase tracking-widest hover:bg-burgundy">Buy Now</Button>
            <Button variant="outline" onClick={() => toggleWishlist(p)} data-testid="wishlist-detail-btn" className="h-11 rounded-none border-burgundy px-4 text-burgundy hover:bg-burgundy hover:text-white">
              <Heart className={`h-5 w-5 ${wished ? "fill-burgundy" : ""}`} />
            </Button>
          </div>
          <Button asChild variant="outline" className="mt-3 h-11 w-full rounded-none border-[#25D366] text-[#128C7E] hover:bg-[#25D366] hover:text-white" data-testid="ask-whatsapp-btn">
            <a href={waMessage(settings, waText)} target="_blank" rel="noopener noreferrer">Ask on WhatsApp</a>
          </Button>

          <div className="mt-6 grid grid-cols-3 gap-2 border-y border-border py-4 text-center text-xs text-muted-text">
            <div className="flex flex-col items-center gap-1"><Truck className="h-5 w-5 text-burgundy" />Free Shipping</div>
            <div className="flex flex-col items-center gap-1"><RefreshCw className="h-5 w-5 text-burgundy" />Easy Returns</div>
            <div className="flex flex-col items-center gap-1"><ShieldCheck className="h-5 w-5 text-burgundy" />100% Original</div>
          </div>

          <Tabs defaultValue="desc" className="mt-6">
            <TabsList className="w-full justify-start rounded-none bg-transparent p-0">
              <TabsTrigger value="desc" className="rounded-none" data-testid="tab-desc">Description</TabsTrigger>
              <TabsTrigger value="details" className="rounded-none" data-testid="tab-details">Details</TabsTrigger>
              <TabsTrigger value="shipping" className="rounded-none" data-testid="tab-shipping">Shipping & Returns</TabsTrigger>
            </TabsList>
            <TabsContent value="desc" className="text-sm leading-relaxed text-charcoal">{p.description}</TabsContent>
            <TabsContent value="details" className="space-y-1 text-sm text-charcoal">
              <p><strong>Fabric / Material:</strong> {p.fabric}</p>
              <p><strong>Care:</strong> {p.care}</p>
            </TabsContent>
            <TabsContent value="shipping" className="space-y-1 text-sm text-charcoal">
              <p>{p.shipping_info}</p>
              <p>{p.return_info}</p>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Reviews */}
      <div className="mt-16" id="reviews">
        <h2 className="font-serif text-2xl text-burgundy">Customer Reviews</h2>
        <ReviewForm productId={p.id} onAdded={(rv) => setData({ ...data, reviews: [rv, ...(data.reviews || [])] })} />
        {data.reviews?.length > 0 && (
          <div className="mt-4 space-y-4">
            {data.reviews.map((r) => (
              <div key={r.id} className="border border-border p-4">
                <div className="flex items-center gap-2"><span className="font-medium">{r.name}</span>{r.verified_buyer && <span className="inline-flex items-center gap-1 bg-green-50 px-1.5 py-0.5 text-[10px] font-medium text-green-700" data-testid={`verified-badge-${r.id}`}><BadgeCheck className="h-3 w-3" />Verified Buyer</span>}<div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className={`h-3 w-3 ${i < r.rating ? "fill-amber-400 text-amber-400" : "text-border"}`} />)}</div></div>
                <p className="mt-1 text-sm text-charcoal">{r.comment}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Related */}
      {data.related?.length > 0 && (
        <div className="mt-16">
          <h2 className="font-serif text-2xl text-burgundy">You May Also Like</h2>
          <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-4">
            {data.related.map((rp) => <ProductCard key={rp.id} product={rp} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
